import { useOpenGraphData } from "../hooks";
import { useEffect, useRef, useState } from "react";
import useUtmify from "../utm";
/**
 * Function to extract URLs from a Markdown string.
 * It ignores non-Markdown link previews.
 * @param markdown - The Markdown string to search for URLs.
 * @returns An array of URLs found in the Markdown string.
 */
export function extractMarkdownUrls(markdown: string): string[] {
  // matches [text](url) and extracts the URL
  const markdownLinkRegex = /\[.*?\]\((https?:\/\/[^\s)]+)\)/g;

  const urls: string[] = [];

  let match: RegExpExecArray | null;
  while ((match = markdownLinkRegex.exec(markdown)) !== null) {
    if (match[1]) {
      urls.push(match[1]);
    }
  }
  return urls;
}

const LinkPreview = ({ url }: { url: string }) => {
  const utmify = useUtmify();
  const { metaData, status, error } = useOpenGraphData(url);

  if (status !== "succeeded" || !metaData?.hg_title) {
    return <></>;
  }

  return (
    <a
      href={utmify(metaData.og_url || metaData.hg_url, "message_link")}
      target="_blank"
      rel="noopener noreferrer nofollow"
      aria-label={`Visit External Website (opens in a new tab)`}
      className="block mt-2 ring-gray-200 hover:bg-gray-50 px-2 ring-inset ring-1 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand"
    >
      <div className="w-full flex flex-col sm:flex-row overflow-hidden">
        <div>
          <SmartImage src={metaData.hg_image} />
        </div>
        <div className="px-3 py-2 flex flex-col">
          <p className="font-semibold text-chat-text line-clamp-2">
            {metaData.hg_title}
          </p>
          <p className="text-sm text-chat-text-light sm:line-clamp-3 line-clamp-2">
            {metaData.hg_description}
          </p>
          <p className="mt-auto text-xs text-chat-text-light line-clamp-1">
            {metaData.hg_url}
          </p>
        </div>
      </div>
    </a>
  );
};

const SmartImage = ({
  src,
  alt = "",
  className = "",
}: {
  src: string;
  alt?: string;
  className?: string;
}) => {
  const [objectFit, setObjectFit] = useState("object-cover");
  const [isError, setIsError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    const updateObjectFit = () => {
      if (!imgRef.current) return;

      const { naturalWidth, naturalHeight } = imgRef.current;
      const aspectRatio = naturalWidth / naturalHeight;

      // Square images should be object-contain
      // Landscape images should be object-cover
      // Portrait images should be object-contain
      setObjectFit(aspectRatio > 1.3 ? "object-cover" : "object-contain");
      setIsLoaded(true);
    };

    const img = imgRef.current;

    if (img?.complete) {
      updateObjectFit();
    } else if (img) {
      img.addEventListener("load", updateObjectFit);
      return () => img.removeEventListener("load", updateObjectFit);
    }
  }, []);

  return isError ? (
    <></>
  ) : (
    <img
      ref={imgRef}
      src={src}
      alt={alt}
      className={`max-h-64 w-full max-w-xl sm:h-full sm:max-h-48 ${objectFit} ${className}`}
      onError={() => setIsError(true)}
      referrerPolicy="no-referrer"
      crossOrigin="anonymous"
    />
  );
};

export default LinkPreview;
